import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBookingData } from '../context/BookingDataContext';
import { 
  TrendingUp, Calendar, CreditCard, Banknote, 
  BarChart3, Loader2, ChevronDown, Check, X, RefreshCw, Globe,
  Users, Home, ArrowUpRight, ArrowDownRight, Activity, Download, FileSpreadsheet, FileText
} from 'lucide-react';
import { format, subDays, startOfYesterday, endOfYesterday, startOfDay, endOfDay } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area } from 'recharts';
import OtaLogo from '../components/OtaLogo';
import AppHeader from '../components/AppHeader';
import { bookingsAPI } from '../services/api';
import { getToken } from '../services/tokenService';
import { useToast } from '../hooks/use-toast';

const COLORS = ['#10B981', '#0B1F3A', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

const AnalyticsScreen = () => {
  const { selectedProperty } = useAuth();
  const { toast } = useToast();
  const {
    loading: dataLoading,
    refreshData,
    getAnalytics
  } = useBookingData();
  
  const [filterType, setFilterType] = useState('today');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // Close export menu on outside click / Escape
  useEffect(() => {
    if (!showExportMenu) return;
    const onClick = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowExportMenu(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showExportMenu]);

  const filterOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Custom Range' },
    // iter113 — ALL = complete booking history (no date filter)
    { value: 'all', label: 'All' },
  ];

  const getDateRange = useCallback(() => {
    const today = new Date();
    let startDate, endDate;
    switch (filterType) {
      case 'today':
        startDate = startOfDay(today); endDate = endOfDay(today); break;
      case 'yesterday':
        startDate = startOfYesterday(); endDate = endOfYesterday(); break;
      case 'weekly':
        startDate = subDays(today, 6); endDate = today; break;       // last 7 days inclusive
      case 'monthly':
        startDate = subDays(today, 29); endDate = today; break;      // last 30 days inclusive
      case 'custom':
        if (customStartDate && customEndDate) return { start: customStartDate, end: customEndDate };
        startDate = subDays(today, 29); endDate = today; break;
      case 'all':
        // iter113 — All history: use a wide window (2015-01-01 → today+1yr) so every booking is included.
        return { start: '2015-01-01', end: format(new Date(today.getFullYear() + 1, 11, 31), 'yyyy-MM-dd') };
      default:
        startDate = subDays(today, 29); endDate = today;
    }
    return { start: format(startDate, 'yyyy-MM-dd'), end: format(endDate, 'yyyy-MM-dd') };
  }, [filterType, customStartDate, customEndDate]);

  const analytics = useMemo(() => {
    const { start, end } = getDateRange();
    return getAnalytics(start, end);
  }, [getDateRange, getAnalytics]);

  const previousAnalytics = useMemo(() => {
    const { start, end } = getDateRange();
    const startDate = new Date(start);
    const endDate = new Date(end);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const prevEnd = format(subDays(startDate, 1), 'yyyy-MM-dd');
    const prevStart = format(subDays(startDate, daysDiff), 'yyyy-MM-dd');
    return getAnalytics(prevStart, prevEnd);
  }, [getDateRange, getAnalytics]);

  const metrics = useMemo(() => {
    const curr = analytics?.total_revenue || 0;
    const prev = previousAnalytics?.total_revenue || 0;
    const revenueGrowth = prev > 0 ? ((curr - prev) / prev * 100).toFixed(1) : 0;
    const currB = analytics?.total_bookings || 0;
    const prevB = previousAnalytics?.total_bookings || 0;
    const bookingsGrowth = prevB > 0 ? ((currB - prevB) / prevB * 100).toFixed(1) : 0;
    const { start, end } = getDateRange();
    const days = Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) || 1;
    return {
      revenueGrowth: parseFloat(revenueGrowth),
      bookingsGrowth: parseFloat(bookingsGrowth),
      avgBookingsPerDay: (currB / days).toFixed(1),
      avgBookingValue: analytics?.average_booking_value || 0
    };
  }, [analytics, previousAnalytics, getDateRange]);

  const handleRefresh = async () => { setRefreshing(true); await refreshData(); setRefreshing(false); };
  const handleFilterChange = (value) => {
    if (value === 'custom') { setShowCustomRange(true); } else { setShowCustomRange(false); setFilterType(value); }
    setShowFilterDropdown(false);
  };
  const applyCustomRange = () => { if (customStartDate && customEndDate) { setFilterType('custom'); setShowCustomRange(false); } };

  const handleExport = (fileFormat) => {
    setShowExportMenu(false);
    const { start, end } = getDateRange();
    const token = getToken();
    if (!token) {
      toast({ title: 'Session expired', description: 'Please login again.', variant: 'destructive' });
      return;
    }
    if (!selectedProperty) {
      toast({ title: 'No property selected', description: 'Please choose a property first.', variant: 'destructive' });
      return;
    }
    const url = bookingsAPI.getExportUrl({
      format: fileFormat,
      startDate: start,
      endDate: end,
      grbId: selectedProperty?.grb_id,
      propertyId: selectedProperty?.property_id || selectedProperty?.id,
      propertyName: selectedProperty?.hotel_name || selectedProperty?.name,
      token,
    });
    // Trigger download via hidden anchor (works reliably in PWA standalone mode)
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener noreferrer';
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast({ title: `Export started`, description: `${fileFormat.toUpperCase()} for ${start} → ${end}` });
  };

  // OTA Breakdown pie chart data (count-based)
  const otaCountData = analytics?.ota_breakdown ? 
    Object.entries(analytics.ota_breakdown)
      .map(([name, count], index) => ({ name, value: count, color: COLORS[index % COLORS.length] }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value) : [];

  // OTA Revenue breakdown
  const otaRevenueData = analytics?.ota_revenue_breakdown ? 
    Object.entries(analytics.ota_revenue_breakdown)
      .map(([name, amount], index) => ({ name, value: Math.round(amount), color: COLORS[index % COLORS.length] }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value) : [];

  // Daily check-in trend
  const dailyBookingsData = analytics?.daily_bookings ? 
    Object.entries(analytics.daily_bookings)
      .map(([date, count]) => ({ date: format(new Date(date + 'T00:00:00'), 'MMM d'), bookings: count }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-14) : [];

  const currentFilter = filterOptions.find(f => f.value === filterType);

  if (dataLoading && !analytics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1B2A7B] animate-spin" />
      </div>
    );
  }

  const GrowthBadge = ({ value }) => {
    if (!value || value === 0) return null;
    const positive = value > 0;
    return (
      <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${positive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {Math.abs(value)}%
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24" data-testid="analytics-screen">
      {/* Premium App Header */}
      <AppHeader />

      {/* Sub-header: Analytics title + filter */}
      <div className="bg-[#1B2A7B] px-4 py-4 shadow-premium">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-white tracking-tight">Analytics</h1>
            <span className="text-[10px] uppercase tracking-wider text-gold/80 font-medium">Booking Insights</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu((v) => !v)}
                className="p-2 rounded-lg bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 transition-all hover:scale-105"
                title="Export bookings"
                data-testid="export-bookings-btn"
              >
                <Download className="w-5 h-5" />
              </button>
              {showExportMenu && (
                <div
                  className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 z-30 overflow-hidden"
                  data-testid="export-menu"
                >
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#EEF1FC] transition-colors"
                    data-testid="export-xlsx-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-[#1B2A7B]" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Excel (.xlsx)</div>
                      <div className="text-[10px] text-gray-500">Styled, with summary</div>
                    </div>
                  </button>
                  <div className="h-px bg-gray-100" />
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                    data-testid="export-csv-btn"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">CSV (.csv)</div>
                      <div className="text-[10px] text-gray-500">Plain, for any tool</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
            <button onClick={handleRefresh} disabled={refreshing}
              className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all hover:scale-105 disabled:opacity-50"
              data-testid="refresh-analytics">
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        {/* Filter */}
        <div className="relative">
          <button onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="flex items-center justify-between w-full px-4 py-3 bg-navy-light rounded-lg text-white border border-gray-600"
            data-testid="filter-dropdown-trigger">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#1B2A7B]" />
              <span>{currentFilter?.label || 'Select Period'}</span>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {showFilterDropdown && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-20 overflow-hidden">
              {filterOptions.map((option) => (
                <button key={option.value} onClick={() => handleFilterChange(option.value)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors ${filterType === option.value ? 'bg-[#EEF1FC]' : ''}`}
                  data-testid={`filter-option-${option.value}`}>
                  <span className={filterType === option.value ? 'text-[#1B2A7B] font-medium' : 'text-gray-700'}>{option.label}</span>
                  {filterType === option.value && <Check className="w-4 h-4 text-[#1B2A7B]" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {showCustomRange && (
          <div className="mt-4 p-4 bg-navy-light rounded-xl border border-gray-600">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium">Select Date Range</h4>
              <button onClick={() => setShowCustomRange(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Start Date</label>
                <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-900 text-sm" data-testid="custom-start-date" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">End Date</label>
                <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-900 text-sm" data-testid="custom-end-date" />
              </div>
            </div>
            <button onClick={applyCustomRange} disabled={!customStartDate || !customEndDate}
              className="w-full py-2 bg-[#1B2A7B] text-white font-medium rounded-lg hover:bg-[#1B2A7B]-dark disabled:opacity-50 transition-colors"
              data-testid="apply-custom-range">Apply</button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Summary Cards Row 1: Total Bookings + Revenue */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="card-total-bookings">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-[#EEF1FC] rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#1B2A7B]" />
              </div>
              <GrowthBadge value={metrics.bookingsGrowth} />
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="total-bookings">{analytics?.total_bookings || 0}</p>
            <p className="text-sm text-gray-500">Total Bookings</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="card-total-revenue">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center">
                <Banknote className="w-5 h-5 text-navy" />
              </div>
              <GrowthBadge value={metrics.revenueGrowth} />
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="total-revenue">₹{(analytics?.total_revenue || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-500">Total Revenue</p>
          </div>
        </div>

        {/* Summary Cards Row 2: OTA + Offline + Avg */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm" data-testid="card-ota-bookings">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
              <Globe className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-xl font-bold text-gray-900" data-testid="ota-bookings">{analytics?.ota_bookings || 0}</p>
            <p className="text-xs text-gray-500">OTA Bookings</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm" data-testid="card-offline-bookings">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mb-2">
              <Users className="w-4 h-4 text-orange-600" />
            </div>
            <p className="text-xl font-bold text-gray-900" data-testid="offline-bookings">{analytics?.offline_bookings || 0}</p>
            <p className="text-xs text-gray-500">Walk-in</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm" data-testid="card-avg-value">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mb-2">
              <CreditCard className="w-4 h-4 text-purple-600" />
            </div>
            <p className="text-xl font-bold text-gray-900" data-testid="avg-booking-value">₹{Math.round(metrics.avgBookingValue).toLocaleString()}</p>
            <p className="text-xs text-gray-500">Avg Value</p>
          </div>
        </div>

        {/* OTA Source Breakdown - Pie Chart */}
        {otaCountData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="ota-breakdown-chart">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              OTA Source Breakdown
            </h3>
            <div className="flex items-center">
              <div className="w-1/2 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={otaCountData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                      {otaCountData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} bookings`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-2 pl-2">
                {otaCountData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between" data-testid={`ota-item-${item.name.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <OtaLogo otaName={item.name} showLabel={false} />
                      <span className="text-sm text-gray-700 truncate">{item.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 ml-2">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* OTA Revenue Breakdown - Bar Chart */}
        {otaRevenueData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="ota-revenue-chart">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-[#1B2A7B]" />
              Revenue by Source
            </h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={otaRevenueData} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} width={80} />
                  <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {otaRevenueData.map((entry, index) => (
                      <Cell key={`rev-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Check-in Trends */}
        {dailyBookingsData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="checkin-trends-chart">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#1B2A7B]" />
              Check-in Trends
            </h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyBookingsData}>
                  <defs>
                    <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9CA3AF' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }} />
                  <Area type="monotone" dataKey="bookings" stroke="#10B981" strokeWidth={2} fill="url(#colorBookings)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!analytics || analytics.total_bookings === 0) && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center" data-testid="analytics-empty-state">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No booking data available</p>
            <p className="text-sm text-gray-400 mt-1">Bookings for this period will appear here</p>
          </div>
        )}

        <div className="text-center text-xs text-gray-400 pt-2">
          Analytics from {analytics?.total_bookings || 0} bookings
        </div>
      </div>
    </div>
  );
};

export default AnalyticsScreen;
